import { normalizeCountyName } from "../../utils/county";
import { StateAbbreviation, TIER_II_MANAGER_STATES } from "../states";

/**
 * T2M states that don't have a Fire Department selector in their portal.
 * - MA: State submits to FDs directly
 * - PA: No FD dropdown in portal
 */
export const TIER_II_MANAGER_STATES_WITHOUT_FD_SELECTOR: readonly string[] = ["MA", "PA"];

/**
 * T2M states that don't have an LEPC selector in their portal.
 * - MA: No LEPC dropdown
 * - MN: No LEPC dropdown
 */
export const TIER_II_MANAGER_STATES_WITHOUT_LEPC_SELECTOR: readonly string[] = ["MA", "MN"];

/**
 * T2M states that have municipalities nested within counties.
 * For these states, FDs are fetched per-municipality rather than per-county.
 * - WI: Municipalities are required, FDs are nested under municipalities
 */
export const TIER_II_MANAGER_STATES_WITH_MUNICIPALITIES: readonly string[] = ["PA", "WI"];

/**
 * T2M states where FDs are fetched per-town/municipality but stored flat under county.
 * These states use a different endpoint (firedepartmenttown&TownID=X) instead of
 * the standard county-based FD endpoint.
 * - CT: FDs are keyed by township, need to fetch municipalities first, then FDs per municipality
 */
export const TIER_II_MANAGER_STATES_WITH_FD_BY_TOWN: readonly string[] = ["CT"];

/**
 * Check if a T2M state has a Fire Department selector in its portal.
 */
export function t2mStateHasFdSelector(state: string): boolean {
  return isT2mState(state) && !TIER_II_MANAGER_STATES_WITHOUT_FD_SELECTOR.includes(state);
}

/**
 * Check if a T2M state has an LEPC selector in its portal.
 */
export function t2mStateHasLepcSelector(state: string): boolean {
  return isT2mState(state) && !TIER_II_MANAGER_STATES_WITHOUT_LEPC_SELECTOR.includes(state);
}

import AR from "./AR.json";
import CT from "./CT.json";
import DE from "./DE.json";
import IL from "./IL.json";
import IN from "./IN.json";
import KY from "./KY.json";
import MA from "./MA.json";
import MD from "./MD.json";
import MI from "./MI.json";
import MN from "./MN.json";
import ND from "./ND.json";
import NV from "./NV.json";
import OR from "./OR.json";
import PA from "./PA.json";
import WI from "./WI.json";
import WV from "./WV.json";

type T2MCountyLepcFdItem = {
  value: string;
  display: string;
};

/**
 * T2M agency with county (and optionally municipality) context.
 * Used for autocomplete and validation.
 */
export type T2MAgencyWithContext = {
  value: string; // portal ID
  display: string; // agency name
  county: string; // county name (the key in the JSON)
  municipality?: string; // for states like WI that have municipalities
};

// Some states (like WI) have agencies nested by municipality within county
type T2MAgencyData = T2MCountyLepcFdItem[] | Record<string, T2MCountyLepcFdItem[]>;

type T2MCountyLepcFdMap = {
  updatedAt?: string;
  counties: T2MCountyLepcFdItem[];
  LEPCs: {
    [key: string]: T2MAgencyData;
  };
  FDs: {
    [key: string]: T2MAgencyData;
  };
  municipalities: {
    [key: string]: T2MCountyLepcFdItem[];
  };
};

/**
 * Check if a state is a T2M state that has county dropdown mappings.
 */
export function isT2mState(state: string): state is (typeof TIER_II_MANAGER_STATES)[number] {
  return (TIER_II_MANAGER_STATES as readonly string[]).includes(state);
}

/**
 * Match a geocoded county name to the correct T2M county display value.
 *
 * This function takes a geocoded county name (e.g., "Rock Island County" from AWS)
 * and finds the matching T2M county display value from the state's dropdown options.
 *
 * Different T2M states have different display formats:
 * - IL: "Rock Island  " (no "County", trailing spaces)
 * - IN: "St. Joseph" (no "County")
 * - WI: "Rock County(10062)" (has "County" suffix + code in parens)
 * - KY: "Jefferson County" (has "County" suffix)
 *
 * @param geocodedCounty - The county name from geocoding (e.g., "Rock Island County")
 * @param state - The state abbreviation (must be a T2M state)
 * @returns The matching T2M county display value, or undefined if no match found
 */
export function matchT2mCounty(
  geocodedCounty: string,
  state: StateAbbreviation
): string | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  const normalizedGeocoded = normalizeCountyName(geocodedCounty);

  // Find the T2M county whose normalized display value matches the normalized geocoded county
  const match = t2mData.counties.find((county) => {
    const normalizedT2m = normalizeCountyName(county.display);
    return normalizedT2m === normalizedGeocoded;
  });

  return match?.display;
}

/**
 * Get all T2M county display values for a state.
 *
 * @param state - The state abbreviation (must be a T2M state)
 * @returns Array of T2M county display values, or undefined if not a T2M state
 */
export function getT2mCountyDisplayValues(state: StateAbbreviation): string[] | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  return t2mData.counties.map((county) => county.display);
}

export function getT2mCountyLepcFdByState(state: StateAbbreviation): T2MCountyLepcFdMap {
  switch (state) {
    case "AR":
      return AR;
    case "CT":
      return CT;
    case "DE":
      return DE;
    case "IL":
      return IL;
    case "IN":
      return IN;
    case "KY":
      return KY;
    case "MA":
      return MA;
    case "MD":
      return MD;
    case "MI":
      return MI;
    case "MN":
      return MN;
    case "ND":
      return ND;
    case "NV":
      return NV;
    case "OR":
      return OR;
    case "PA":
      return PA;
    case "WI":
      // The file is too big and causes TS type checking to fail.
      // @ts-ignore
      return WI as T2MCountyLepcFdMap;
    case "WV":
      return WV;
    default:
      throw new Error(`No T2M LEPC ID map for state ${state}`);
  }
}

/**
 * Find the T2M county key that matches a given county display value.
 * The T2M JSON files use county names as keys for LEPCs and FDs mappings,
 * which may have trailing spaces or other formatting quirks.
 *
 * @param countyDisplay - The county display value to find a key for
 * @param state - The state abbreviation (must be a T2M state)
 * @returns The T2M county key, or undefined if no match found
 */
function findT2mCountyKey(countyDisplay: string, state: StateAbbreviation): string | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  const normalizedInput = normalizeCountyName(countyDisplay);

  // First, check if there's an exact key match (LEPCs/FDs use county display as key)
  // Note: Some states use the county name as key without trailing spaces
  for (const key of Object.keys(t2mData.LEPCs)) {
    if (normalizeCountyName(key) === normalizedInput) {
      return key;
    }
  }

  // Also check FDs keys in case they differ
  for (const key of Object.keys(t2mData.FDs)) {
    if (normalizeCountyName(key) === normalizedInput) {
      return key;
    }
  }

  return undefined;
}

/**
 * Get the valid LEPCs for a county in a T2M state.
 *
 * @param county - The facility's county (should be the T2M display value)
 * @param state - The state abbreviation (must be a T2M state)
 * @returns Array of valid LEPC display values for the county, or undefined if not a T2M state
 */
export function getValidLepcsForCounty(
  county: string,
  state: StateAbbreviation
): string[] | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  const countyKey = findT2mCountyKey(county, state);

  if (!countyKey) {
    return [];
  }

  const lepcsData = t2mData.LEPCs[countyKey];
  return flattenAgencyData(lepcsData).map((lepc) => lepc.display);
}

/**
 * Get the valid FD display names for a county in a T2M state.
 *
 * @param county - The facility's county (should be the T2M display value)
 * @param state - The state abbreviation (must be a T2M state)
 * @returns Array of valid FD display values for the county, or undefined if not a T2M state
 */
export function getValidFdsForCounty(
  county: string,
  state: StateAbbreviation
): string[] | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  const countyKey = findT2mCountyKey(county, state);

  if (!countyKey) {
    return [];
  }

  const fdsData = t2mData.FDs[countyKey];
  return flattenAgencyData(fdsData).map((fd) => fd.display);
}

/**
 * Flatten nested agency data structure.
 * Some states (like WI) have agencies nested by municipality within county.
 * This function handles both flat arrays and nested objects.
 *
 * @param data - Either an array of items or an object with arrays as values
 * @returns Flattened array of items
 */
function flattenAgencyData(
  data: T2MCountyLepcFdItem[] | Record<string, T2MCountyLepcFdItem[]> | undefined
): T2MCountyLepcFdItem[] {
  if (!data) {
    return [];
  }

  // If it's already an array, return it
  if (Array.isArray(data)) {
    return data;
  }

  // If it's a nested object (like WI's FDs by municipality), flatten all values
  return Object.values(data).flat();
}

/**
 * Get the valid FD T2M portal values (IDs) for a county in a T2M state.
 *
 * These values correspond to the `value` field in the T2M JSON and should
 * match the `portalId` field on our FireDepartment records.
 *
 * @param county - The facility's county (should be the T2M display value)
 * @param state - The state abbreviation (must be a T2M state)
 * @returns Array of valid T2M FD values for the county, or undefined if not a T2M state
 */
export function getValidFdT2mValuesForCounty(
  county: string,
  state: StateAbbreviation
): string[] | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  const countyKey = findT2mCountyKey(county, state);

  if (!countyKey) {
    return [];
  }

  const fdsData = t2mData.FDs[countyKey];
  return flattenAgencyData(fdsData).map((fd) => fd.value);
}

/**
 * Get the valid LEPC T2M portal values (IDs) for a county in a T2M state.
 *
 * These values correspond to the `value` field in the T2M JSON and should
 * match the `portalId` field on our Lepc records.
 *
 * @param county - The facility's county (should be the T2M display value)
 * @param state - The state abbreviation (must be a T2M state)
 * @returns Array of valid T2M LEPC values for the county, or undefined if not a T2M state
 */
export function getValidLepcT2mValuesForCounty(
  county: string,
  state: StateAbbreviation
): string[] | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  const countyKey = findT2mCountyKey(county, state);

  if (!countyKey) {
    return [];
  }

  const lepcsData = t2mData.LEPCs[countyKey];
  return flattenAgencyData(lepcsData).map((lepc) => lepc.value);
}

/**
 * Get all LEPCs for a T2M state with county context.
 * This is useful for populating autocomplete dropdowns.
 * Results are deduped by portal ID (value), keeping the first occurrence.
 *
 * @param state - The state abbreviation (must be a T2M state)
 * @returns Array of LEPCs with county info, or undefined if not a T2M state
 */
export function getAllLepcsForState(state: StateAbbreviation): T2MAgencyWithContext[] | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  const result: T2MAgencyWithContext[] = [];
  const seenValues = new Set<string>();

  for (const [countyKey, lepcsData] of Object.entries(t2mData.LEPCs)) {
    const lepcs = flattenAgencyData(lepcsData);
    for (const lepc of lepcs) {
      // Dedupe by portal ID (value)
      if (!seenValues.has(lepc.value)) {
        seenValues.add(lepc.value);
        result.push({
          value: lepc.value,
          display: lepc.display,
          county: countyKey,
        });
      }
    }
  }

  return result;
}

/**
 * Get all Fire Departments for a T2M state with county/municipality context.
 * This is useful for populating autocomplete dropdowns.
 * For states like WI, FDs are nested by municipality - this info is preserved.
 * Results are deduped by portal ID (value), keeping the first occurrence.
 *
 * @param state - The state abbreviation (must be a T2M state)
 * @returns Array of FDs with county and optional municipality info, or undefined if not a T2M state
 */
export function getAllFdsForState(state: StateAbbreviation): T2MAgencyWithContext[] | undefined {
  if (!isT2mState(state)) {
    return undefined;
  }

  const t2mData = getT2mCountyLepcFdByState(state);
  const result: T2MAgencyWithContext[] = [];
  const seenValues = new Set<string>();

  for (const [countyKey, fdsData] of Object.entries(t2mData.FDs)) {
    // Check if data is nested by municipality (like WI)
    if (fdsData && !Array.isArray(fdsData)) {
      // Nested structure: { "Municipality Name": [...fds] }
      for (const [municipalityKey, fds] of Object.entries(
        fdsData as Record<string, T2MCountyLepcFdItem[]>
      )) {
        for (const fd of fds) {
          // Dedupe by portal ID (value)
          if (!seenValues.has(fd.value)) {
            seenValues.add(fd.value);
            result.push({
              value: fd.value,
              display: fd.display,
              county: countyKey,
              municipality: municipalityKey,
            });
          }
        }
      }
    } else {
      // Flat array
      const fds = flattenAgencyData(fdsData);
      for (const fd of fds) {
        // Dedupe by portal ID (value)
        if (!seenValues.has(fd.value)) {
          seenValues.add(fd.value);
          result.push({
            value: fd.value,
            display: fd.display,
            county: countyKey,
          });
        }
      }
    }
  }

  return result;
}

/**
 * Check if a portal ID is a valid LEPC for the given state.
 * Searches across all counties in the state.
 *
 * @param portalId - The portal ID to validate
 * @param state - The state abbreviation (must be a T2M state with LEPC selector)
 * @returns true if the portal ID is valid, false otherwise
 */
export function isValidLepcPortalIdForState(portalId: string, state: StateAbbreviation): boolean {
  if (!isT2mState(state) || !t2mStateHasLepcSelector(state)) {
    return true; // Not a T2M state with LEPC selector, so no validation needed
  }

  const allLepcs = getAllLepcsForState(state);
  if (!allLepcs) {
    return true;
  }

  return allLepcs.some((lepc) => lepc.value === portalId);
}

/**
 * Check if a portal ID is a valid Fire Department for the given state.
 * Searches across all counties in the state.
 *
 * @param portalId - The portal ID to validate
 * @param state - The state abbreviation (must be a T2M state with FD selector)
 * @returns true if the portal ID is valid, false otherwise
 */
export function isValidFdPortalIdForState(portalId: string, state: StateAbbreviation): boolean {
  if (!isT2mState(state) || !t2mStateHasFdSelector(state)) {
    return true; // Not a T2M state with FD selector, so no validation needed
  }

  const allFds = getAllFdsForState(state);
  if (!allFds) {
    return true;
  }

  return allFds.some((fd) => fd.value === portalId);
}

/**
 * Get the expected display name for an FD portal ID.
 *
 * @param portalId - The portal ID to look up
 * @param state - The state abbreviation (must be a T2M state with FD selector)
 * @returns The display name for the portal ID, or undefined if not found
 */
export function getFdNameForPortalId(
  portalId: string,
  state: StateAbbreviation
): string | undefined {
  if (!isT2mState(state) || !t2mStateHasFdSelector(state)) {
    return undefined;
  }

  const allFds = getAllFdsForState(state);
  if (!allFds) {
    return undefined;
  }

  return allFds.find((fd) => fd.value === portalId)?.display;
}

/**
 * Get the expected display name for an LEPC portal ID.
 *
 * @param portalId - The portal ID to look up
 * @param state - The state abbreviation (must be a T2M state with LEPC selector)
 * @returns The display name for the portal ID, or undefined if not found
 */
export function getLepcNameForPortalId(
  portalId: string,
  state: StateAbbreviation
): string | undefined {
  if (!isT2mState(state) || !t2mStateHasLepcSelector(state)) {
    return undefined;
  }

  const allLepcs = getAllLepcsForState(state);
  if (!allLepcs) {
    return undefined;
  }

  return allLepcs.find((lepc) => lepc.value === portalId)?.display;
}

/**
 * Find an FD by name and return its portal ID and display name.
 * Useful for checking if a name matches a different agency.
 *
 * @param name - The name to search for
 * @param state - The state abbreviation (must be a T2M state with FD selector)
 * @returns The matching FD info, or undefined if not found
 */
export function findFdByName(
  name: string,
  state: StateAbbreviation
): T2MAgencyWithContext | undefined {
  if (!isT2mState(state) || !t2mStateHasFdSelector(state)) {
    return undefined;
  }

  const allFds = getAllFdsForState(state);
  if (!allFds) {
    return undefined;
  }

  return allFds.find((fd) => fd.display === name);
}

/**
 * Find an LEPC by name and return its portal ID and display name.
 * Useful for checking if a name matches a different agency.
 *
 * @param name - The name to search for
 * @param state - The state abbreviation (must be a T2M state with LEPC selector)
 * @returns The matching LEPC info, or undefined if not found
 */
export function findLepcByName(
  name: string,
  state: StateAbbreviation
): T2MAgencyWithContext | undefined {
  if (!isT2mState(state) || !t2mStateHasLepcSelector(state)) {
    return undefined;
  }

  const allLepcs = getAllLepcsForState(state);
  if (!allLepcs) {
    return undefined;
  }

  return allLepcs.find((lepc) => lepc.display === name);
}
